import {
  EventOutlined,
  InsertDriveFileOutlined,
  SummarizeOutlined,
} from "@mui/icons-material";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import {
  ETL_RUN_DATE_TIME_FORMAT,
  formatDate,
  IEtlJobSummary,
  NO_VALUE_DISPLAY,
  useTimezone,
} from "@ryde/shared";

import { EtlSummaryCardStyled } from "./etl-summary.styled";
import {
  formatWorkerLabel,
  resolveEtlSummaryTiles,
  resolveEtlSummaryTitleKey,
} from "./etl-summary.utils";

export interface IEtlSummaryCardProps {
  summary?: IEtlJobSummary;
  finishedAt?: TDateISO | null;
  rowsRead?: number;
  rowsWritten?: number;
}

/**
 * Per-run ETL summary, shared by the admin job-log panel and the customer Data Uploads panel. Renders
 * only what its props carry: the customer response omits `addedWorkers`, so its chips do not appear.
 */
export const EtlSummaryCard: FC<IEtlSummaryCardProps> = ({
  summary,
  finishedAt,
  rowsRead,
  rowsWritten,
}) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();

  if (!summary) {
    return null;
  }

  const uploadDate = finishedAt
    ? formatDate({
        dateISO: finishedAt,
        dateFormat: ETL_RUN_DATE_TIME_FORMAT,
        timezone,
      })
    : NO_VALUE_DISPLAY;
  const addedWorkers = summary.addedWorkers ?? [];
  const tiles = resolveEtlSummaryTiles({ summary, rowsRead, rowsWritten });

  return (
    <EtlSummaryCardStyled>
      <div className="header">
        <div className="header__title">
          <SummarizeOutlined />
          <span>{t(resolveEtlSummaryTitleKey(summary))}</span>
        </div>

        <div className="header__meta">
          <span className="header__meta-item">
            <InsertDriveFileOutlined fontSize="small" />
            {summary.fileName ?? NO_VALUE_DISPLAY}
          </span>
          <span className="header__meta-item">
            <EventOutlined fontSize="small" />
            {uploadDate}
          </span>
        </div>
      </div>

      <div className="body">
        <div className="stats">
          {tiles.map((tile, index) => (
            <div
              key={`${tile.variant}-${tile.labelKey}-${index}`}
              className={`stat stat--${tile.variant}`}
            >
              <span className="stat__label">{t(tile.labelKey)}</span>
              <span className="stat__value">{tile.value}</span>
            </div>
          ))}
        </div>

        {addedWorkers.length > 0 && (
          <div className="detail">
            <div className="detail__label">
              {t("etlJobLog.summaryLabel.newWorkersDetail")}
            </div>
            <div className="chips">
              {addedWorkers.map((worker, index) => {
                const key = `${worker.code ?? worker.firstName ?? "worker"}-${index}`;
                const label = formatWorkerLabel(worker);

                return (
                  <span key={key} className="chip">
                    {label}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </EtlSummaryCardStyled>
  );
};
